import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { LearnArticleLink } from '@/features/aprender/learnArticleShared'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const ARTICLE_ID = 'hacemos-cocina-guia-repsol-tipi'
const k = (f: string) => `news.articles.${ARTICLE_ID}.${f}`

const REF = {
    guiaQueEs: {
        href: 'https://www.guiarepsol.com/profesionales/articulos/que-es-hacemos-cocina',
        domain: 'guiarepsol.com',
        label: 'Guía Repsol · Qué es Hacemos Cocina',
    },
    guiaHerramientas: {
        href: 'https://www.guiarepsol.com/profesionales/herramientas/',
        domain: 'guiarepsol.com',
        label: 'Guía Repsol · Herramientas',
    },
    guiaTipi: {
        href: 'https://www.guiarepsol.com/profesionales/herramientas/tipi/',
        domain: 'guiarepsol.com',
        label: 'Guía Repsol · Tipi',
    },
    guiaCondiciones: {
        href: 'https://www.guiarepsol.com/profesionales/condiciones-servicio/',
        domain: 'guiarepsol.com',
        label: 'Guía Repsol · Condiciones',
    },
    guiaRegistro: {
        href: 'https://www.guiarepsol.com/profesionales/establecimientos/',
        domain: 'guiarepsol.com',
        label: 'Guía Repsol · Registro',
    },
    habraQueComer: {
        href: 'https://habraquecomer.com/repsol-guia-hacemos-cocina-santander-profesionales-hosteleria/',
        domain: 'habraquecomer.com',
        label: 'Habrá que comer · Santander',
    },
    forbes: {
        href: 'https://forbes.es/economia/835060/repsol-presenta-la-guia-hacemos-cocina-destinada-a-acompanar-a-los-profesionales-de-la-hosteleria/',
        domain: 'forbes.es',
        label: 'Forbes España',
    },
    anuario: {
        href: 'https://hosteleriadeespana.es/publicaciones-hosteleria.html',
        domain: 'hosteleriadeespana.es',
        label: 'Anuario Hostelería de España',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

function ExtLink({ href, children }: { href: string; children: ReactNode }) {
    return (
        <a href={href} target='_blank' rel='noopener noreferrer' className={article.link}>
            {children}
        </a>
    )
}

export function HacemosCocinaTipiArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'que-es', label: t(k('toc.queEs'), '¿Qué es Hacemos Cocina?') },
        { id: 'dia-31', label: t(k('toc.dia31'), '¿Qué pasa cuando se acaba el mes gratis?') },
        { id: 'por-que-tipi', label: t(k('toc.porQue'), 'Por qué Tipi está en Hacemos Cocina') },
        { id: 'cupo', label: t(k('toc.cupo'), '¿Cuántos hosteleros van a poder aprovecharlo?') },
        { id: 'alta', label: t(k('toc.alta'), 'Cómo darte de alta y activar Tipi') },
        { id: 'diez-platos', label: t(k('toc.diezPlatos'), 'Un mes, diez platos y una cuenta honesta') },
        { id: 'antes-cupo', label: t(k('toc.antes'), 'Antes de que se acabe el cupo') },
        { id: 'limites', label: t(k('toc.limites'), 'Lo que no podemos prometerte') },
    ]

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo de Hacemos Cocina y Tipi y quiero activar el mes cubierto (o que me ayudéis con el alta).',
        ),
    )

    const signupSteps: ReactNode[] = [
        <>
            {t(k('alta.1.before'), 'Entra en la ')}
            <ExtLink href={REF.guiaRegistro.href}>
                {t(k('alta.1.link'), 'página de registro de Hacemos Cocina')}
            </ExtLink>
            {t(k('alta.1.after'), ' y elige cómo darte de alta: correo, Apple, Google o Facebook.')}
        </>,
        t(
            k('alta.2'),
            'Rellena nombre, apellidos, móvil y contraseña. La contraseña pide 8 caracteres como mínimo, con mayúscula, minúscula, número y símbolo.',
        ),
        <>
            {t(k('alta.3.before'), 'Acepta las condiciones legales. ')}
            <strong>{t(k('alta.3.strong'), 'La casilla de descuentos y ofertas es opcional')}</strong>
            {t(k('alta.3.after'), ', y si la marcas aceptas todos los tratamientos de datos que lista.')}
        </>,
        t(
            k('alta.4'),
            'Completa los datos del establecimiento: nombre, CIF, dirección, código postal, municipio, tipo de local, tu función y teléfono público. Al final certificas que eres representante autorizado.',
        ),
        t(k('alta.5'), 'Abre el correo de verificación y pulsa el enlace. Si no llega, mira en spam.'),
        t(k('alta.6'), 'Ya dentro, ve a Herramientas, abre Tipi y elige el establecimiento.'),
        t(
            k('alta.7'),
            'Al entrar en Tipi la primera vez te pedimos el móvil, para llamarte y montar contigo los primeros platos.',
        ),
    ]

    const beforeQuota: ReactNode[] = [
        <>
            <strong>{t(k('antes.1.strong'), 'Actívalo ya si te interesa.')}</strong>{' '}
            {t(k('antes.1.after'), 'El cupo va por orden de llegada y no espera a que tengas un rato.')}
        </>,
        <>
            <strong>{t(k('antes.2.strong'), 'Junta los albaranes de la última semana')}</strong>{' '}
            {t(k('antes.2.after'), 'de tus proveedores principales. Son los que actualizan los precios de tus platos.')}
        </>,
        <>
            <strong>{t(k('antes.3.strong'), 'Elige los diez platos que más vendes.')}</strong>{' '}
            {t(k('antes.3.after'), 'Es donde un céntimo mal calculado se multiplica por cada ración.')}
        </>,
        <>
            <strong>{t(k('antes.4.strong'), 'Apunta en el calendario el día 31 desde el alta.')}</strong>{' '}
            {t(k('antes.4.after'), 'Ese día decides si sigues, con números delante y no con una sensación.')}
        </>,
    ]

    const limits: ReactNode[] = [
        <>
            <strong>{t(k('limites.1.strong'), 'No damos una cifra de licencias restantes.')}</strong>{' '}
            {t(
                k('limites.1.after'),
                'Cambia con cada alta, y cualquier número que pusiéramos hoy estaría viejo mañana.',
            )}
        </>,
        <>
            <strong>{t(k('limites.2.strong'), 'No hay fecha de cierre publicada.')}</strong>{' '}
            {t(k('limites.2.after'), 'Si las 150 licencias se agotan antes, la oferta termina antes.')}
        </>,
        <>
            <strong>{t(k('limites.3.strong'), 'Las cifras de la plataforma se mueven.')}</strong>{' '}
            {t(
                k('limites.3.after'),
                'El contador de hosteleros activos marca hoy más de 4.000. En otra versión de la misma web marcaba más de 1.000. Ninguna de las dos lleva fecha ni explica qué cuenta como activo.',
            )}
        </>,
        <>
            <strong>{t(k('limites.4.strong'), 'Los encuentros tampoco cuadran del todo.')}</strong>{' '}
            {t(
                k('limites.4.after'),
                'En el lanzamiento se anunciaron entre 15 y 20 al año. La web habla ahora de más de 24.',
            )}
        </>,
    ]

    const sources = [
        {
            label: t(k('sources.1'), 'Guía Repsol. Hacemos Cocina: el espacio para quienes estáis al frente'),
            href: REF.guiaQueEs.href,
        },
        {
            label: t(k('sources.2'), 'Guía Repsol. Herramientas de Hacemos Cocina'),
            href: REF.guiaHerramientas.href,
        },
        {
            label: t(k('sources.3'), 'Guía Repsol. Tipi en Hacemos Cocina'),
            href: REF.guiaTipi.href,
        },
        {
            label: t(k('sources.4'), 'Guía Repsol. Condiciones del servicio de Hacemos Cocina'),
            href: REF.guiaCondiciones.href,
        },
        {
            label: t(k('sources.5'), 'Habrá que comer. Repsol presenta en Santander la nueva guía «Hacemos Cocina»'),
            href: REF.habraQueComer.href,
        },
        {
            label: t(k('sources.6'), 'Forbes España. Repsol presenta la Guía «Hacemos cocina»'),
            href: REF.forbes.href,
        },
        {
            label: t(k('sources.7'), 'Anuario de la Hostelería de España, Hostelería de España'),
            href: REF.anuario.href,
        },
    ]

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(
                                k('dek.before'),
                                'Hacemos Cocina, la plataforma gratuita de Guía Repsol para profesionales de la hostelería, cubre el primer mes de Tipi a quien se registre en ella. Ese mes incluye ',
                            )}
                            <strong>{t(k('dek.albaranes'), '100 albaranes')}</strong>
                            {t(k('dek.mid1'), ' y no pide tarjeta. Si quieres seguir, desde el segundo mes son ')}
                            <strong>{t(k('dek.precio'), '39 € + IVA')}</strong>
                            {t(k('dek.mid2'), ' al mes, sin permanencia. El cupo es de ')}
                            <strong>{t(k('dek.cupo'), '150 licencias')}</strong>
                            {t(k('dek.after'), ' y cada cuenta activa Tipi en un solo establecimiento.')}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(k('heroAlt'), 'Albaranes y móvil sobre la encimera de una cocina profesional')}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <ArticleReadingLayout
                    intro={
                        <div className={article.prose}>
                            <p className={cn(article.p, 'mt-0')}>
                                {t(
                                    k('open.p1'),
                                    'Lo gratis sale caro. Lo hemos oído en todas las barras, y casi siempre con razón.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('open.p2'),
                                    'La prueba gratis que te pide la tarjeta el primer día y te cobra el día 31 sin avisar.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('open.p3'),
                                    'Por eso empezamos por el final: aquí lo que pasa el día 31 está escrito antes de que te apuntes.',
                                )}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <section id='que-es'>
                        <h2 className={cn(article.h2, 'mt-0')}>
                            {t(k('h2.queEs'), '¿Qué es Hacemos Cocina de la Guía Repsol?')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('queEs.p1.strong'),
                                        'Hacemos Cocina es la plataforma de Guía Repsol para quienes sacan adelante un bar, un restaurante, una cafetería o un chiringuito.',
                                    )}
                                </strong>
                            </p>
                            <p className={cn(article.p, article.pGapXl)}>
                                {t(k('queEs.p2.before'), 'Se ')}
                                <span className={article.cite}>
                                    <ExtLink href={REF.habraQueComer.href}>
                                        {t(k('queEs.p2.link'), 'presentó en Santander el 26 de noviembre de 2025')}
                                    </ExtLink>
                                    <SourceChip source='habraQueComer' />
                                </span>{' '}
                                {t(
                                    k('queEs.p2.after'),
                                    'y combina encuentros presenciales por toda España con una plataforma digital de contenidos, formación y herramientas.',
                                )}
                            </p>
                        </div>
                        <div className={article.tableWrap}>
                            <table className={article.tableFacts}>
                                <tbody>
                                    <tr>
                                        <th scope='row'>{t(k('table.registro.label'), 'Registro')}</th>
                                        <td>{t(k('table.registro.value'), 'Gratuito')}</td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>{t(k('table.incluye.label'), 'Qué incluye')}</th>
                                        <td>
                                            {t(
                                                k('table.incluye.value'),
                                                'Tu ficha y tus reseñas en Guía Repsol, encuentros, podcast, formaciones y herramientas',
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>{t(k('table.herramientas.label'), 'Herramientas activas')}</th>
                                        <td>
                                            {t(
                                                k('table.herramientas.value'),
                                                'Tipi, Shifty, Fandit, calculadora de CAE, generador de QR, calculadora de sostenibilidad',
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>{t(k('table.tipi.label'), 'Lo que te da con Tipi')}</th>
                                        <td>{t(k('table.tipi.value'), 'Primer mes cubierto, con 100 albaranes')}</td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>{t(k('table.despues.label'), 'Después del primer mes')}</th>
                                        <td>
                                            {t(k('table.despues.value'), '39 € + IVA al mes, solo si decides seguir')}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id='dia-31'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.dia31'), '¿Qué pasa cuando se acaba el mes gratis?')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>{t(k('dia31.p1.strong'), 'Durante el primer mes no metes tarjeta.')}</strong>{' '}
                                {t(
                                    k('dia31.p1.mid'),
                                    'Al terminar, si quieres seguir con Tipi, añades un método de pago y pasas a ',
                                )}
                                <strong>{t(k('dia31.p1.precio'), '39 € + IVA al mes, 47,19 € con IVA')}</strong>
                                {t(k('dia31.p1.after'), '.')}
                            </p>
                            <p className={cn(article.p, article.pGapMd)}>
                                {t(
                                    k('dia31.p2'),
                                    'Si no lo añades, no se te cobra nada. No hay permanencia ni penalización por irte.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGapMd)}>
                                {t(k('dia31.p3'), 'Dos cosas más antes de apuntarte.')}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(k('dia31.p4.strong'), 'Solo puedes activar Tipi en un establecimiento.')}
                                </strong>{' '}
                                {t(
                                    k('dia31.p4.after'),
                                    'Si tienes dos locales, elige el que más te preocupe, no el que mejor funciona.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(k('dia31.p5.strong'), 'Los 100 albaranes son el paquete del primer mes.')}
                                </strong>{' '}
                                {t(
                                    k('dia31.p5.after'),
                                    'Un local que recibe tres o cuatro albaranes al día los gasta en cerca de un mes.',
                                )}{' '}
                                <em>
                                    {t(
                                        k('dia31.p5.em'),
                                        'Cálculo nuestro con un ritmo de entregas habitual, no una media medida.',
                                    )}
                                </em>{' '}
                                {t(k('dia31.p5.tail'), 'Los que pases de ahí se compran aparte, como saldo.')}
                            </p>
                        </div>
                    </section>

                    <section id='por-que-tipi'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.porQue'), 'Por qué Tipi está en Hacemos Cocina')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('porQue.p1.before'),
                                    'Esto lo escribe Tipi, que es una de las herramientas de la plataforma. Tómalo como lo que es: nuestra parte, contada por nosotros.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('porQue.p2.before'),
                                    'Tipi nació para que controlar los números de un restaurante deje de ser cosa de grupos con controller y departamento financiero. Hacemos Cocina nació para acompañar al hostelero en el día a día. Intentamos lo mismo desde sitios distintos.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('porQue.p3'), 'El precio sale de la misma cuenta que haces tú con tu carta.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('porQue.p4'),
                                    'Un hostelero cobra lo necesario para que su negocio sobreviva y pueda seguir viviendo de lo que le gusta. Ni un euro más de lo que el plato aguanta, ni uno menos de lo que cuesta hacerlo.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('porQue.p5'),
                                    'Nosotros aplicamos la misma regla. No hemos puesto 39 € para ganar una comparativa. Los hemos puesto porque no creemos que haya que cobrarle de más a nadie.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('porQue.p6'),
                                    'Si el producto merece la pena, lo demuestra el uso, no el precio. Que Guía Repsol nos haya querido en su plataforma es el aval que más nos pesa.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('porQue.p7'),
                                    'Quitarte el primer mes es la forma más directa que conocemos de romper la barrera de siempre: probar sin arriesgar caja.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='cupo'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.cupo'), '¿Cuántos hosteleros van a poder aprovecharlo?')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>{t(k('cupo.p1.strong'), 'El cupo es de 150 licencias.')}</strong>{' '}
                                {t(k('cupo.p1.mid'), 'La plataforma dice tener ')}
                                <span className={article.cite}>
                                    <ExtLink href={REF.guiaQueEs.href}>
                                        {t(k('cupo.p1.link'), 'más de 4.000 hosteleros activos')}
                                    </ExtLink>
                                    <SourceChip source='guiaQueEs' />
                                </span>
                                .
                            </p>
                            <p className={article.p}>
                                {t(k('cupo.p2.before'), '150 ÷ 4.000 = ')}
                                <strong>{t(k('cupo.p2.pct'), '3,75 %')}</strong>
                                {t(
                                    k('cupo.p2.after'),
                                    '. Menos de 4 de cada 100 usuarios de Hacemos Cocina pueden activar el mes cubierto.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('cupo.p3.before'), 'Frente a los ')}
                                <span className={article.cite}>
                                    {t(k('cupo.p3.cite'), 'más de 300.000 establecimientos de hostelería de España')}
                                    <SourceChip source='anuario' />
                                </span>
                                {t(
                                    k('cupo.p3.mid'),
                                    ', según el Anuario de la Hostelería de España, 150 licencias son el ',
                                )}
                                <strong>{t(k('cupo.p3.pct'), '0,05 %')}</strong>
                                {t(k('cupo.p3.after'), ' del sector.')}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('cupo.p4'),
                                    'No es una campaña para todo el sector: es una tanda corta, y el primero que llega se la lleva.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='alta'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.alta'), 'Cómo darte de alta y activar Tipi, paso a paso')}
                        </h2>
                        <div className={article.prose}>
                            <p className={cn(article.p, article.pGapMd)}>
                                {t(
                                    k('alta.intro'),
                                    'Antes de empezar, ten a mano el CIF y la dirección del local. Es lo único que te puede parar a mitad.',
                                )}
                            </p>
                            <NewsArticleNumList items={signupSteps} />
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('alta.browser.strong'),
                                        'Tipi funciona en el navegador del móvil, la tablet o el ordenador.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('alta.browser.mid'),
                                    'No hay que instalar nada, solo dar permiso al micrófono, como explica la ',
                                )}
                                <span className={article.cite}>
                                    <ExtLink href={REF.guiaTipi.href}>
                                        {t(k('alta.browser.link'), 'ficha de Tipi en Hacemos Cocina')}
                                    </ExtLink>
                                    <SourceChip source='guiaTipi' />
                                </span>
                                .
                            </p>
                        </div>
                    </section>

                    <section id='diez-platos'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.diezPlatos'), 'Un mes, diez platos y una cuenta honesta')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('diez.p1'),
                                    'Pon que tienes un bar de menú y dedicas el mes cubierto a tus diez platos más vendidos.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('diez.p2.before'), 'Coste del mes: ')}
                                <strong>{t(k('diez.p2.cost'), '0 €')}</strong>
                                {t(k('diez.p2.after'), '.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('diez.p3'),
                                    'Uno de esos platos lo vendes 15 veces al día. Al escandallarlo con los precios de los últimos albaranes, ves que te deja 0,60 € menos por ración de lo que creías, porque el aceite y la proteína han subido desde la última vez que hiciste la cuenta.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('diez.p4.before'), '0,60 € × 15 raciones × 30 días = ')}
                                <strong>{t(k('diez.p4.result'), '270 € al mes')}</strong>
                                {t(k('diez.p4.mid'), ' que se iban sin que lo vieras. ')}
                                <em>
                                    {t(
                                        k('diez.p4.em'),
                                        'Números inventados para el ejemplo, no el resultado de ningún cliente.',
                                    )}
                                </em>
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('diez.p5'),
                                    'Si encuentras algo así, seguir te cuesta 47,19 € al mes. Si en tus diez platos no aparece nada, también es información: tu carta aguanta, y te vas sin haber pagado.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(k('diez.p6.before'), 'Si nunca has hecho un escandallo, aquí tienes ')}
                                <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                    {t(k('diez.p6.link'), 'el método para calcular el coste de un plato desde cero')}
                                </LearnArticleLink>
                                {t(k('diez.p6.after'), '.')}
                            </p>
                        </div>
                    </section>

                    <section id='antes-cupo'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.antes'), 'Antes de que se acabe el cupo')}
                        </h2>
                        <div className={article.prose}>
                            <p className={cn(article.p, article.pGapMd)}>
                                {t(k('antes.intro'), 'Ordenado por lo que más tarda.')}
                            </p>
                            <NewsArticleNumList items={beforeQuota} />
                        </div>
                    </section>

                    <section id='limites'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.limites'), 'Lo que no podemos prometerte')}
                        </h2>
                        <div className={article.prose}>
                            <NewsArticleNumList items={limits} />
                            <p className={article.p}>
                                {t(
                                    k('closing.p1'),
                                    'Si todavía no conocías esta iniciativa y estabas con dudas sobre cómo probar Tipi, regístrate en Hacemos Cocina y aprovecha la oportunidad.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('closing.p2'),
                                    'Lo gratis solo sale caro cuando no sabes qué pasa el día 31. Ahora ya lo sabes.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section className={article.cta}>
                        <h2 className={cn(article.h2, article.h2Cta)}>{t(k('h2.cta'), '¿Quieres el mes cubierto?')}</h2>
                        <p className={article.ctaSub}>
                            {t(
                                k('cta.sub'),
                                'Regístrate en Hacemos Cocina, activa Tipi y, si te atascas en el alta, escríbenos.',
                            )}
                        </p>
                        <div className={article.ctaActionsCenter}>
                            <a
                                href={REF.guiaRegistro.href}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={cn(article.btn, article.btnSolid)}
                            >
                                {t(k('cta.register'), 'Registrarme en Hacemos Cocina')}
                            </a>
                            <a
                                href={contactWhatsapp}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={cn(article.btn, article.btnOutline)}
                            >
                                {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                            </a>
                        </div>
                    </section>

                    <section className={article.sources} aria-labelledby='fuentes-heading'>
                        <h2 id='fuentes-heading' className={article.sourcesTitle}>
                            {t(k('sourcesTitle'), 'Fuentes')}
                        </h2>
                        <ul className={article.sourcesList}>
                            {sources.map(source => (
                                <li key={source.href}>
                                    <a
                                        href={source.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {source.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </section>
                </ArticleReadingLayout>
            </article>
        </div>
    )
}
